import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {BffUser} from "../../types";
import type {TopicEntrySource} from "../content/topicEntryNote";

export const bffApi = createApi({
    reducerPath: "bffApi",
    baseQuery: fetchBaseQuery({baseUrl: "/"}),
    endpoints: (builder) => ({
        getUser: builder.query<BffUser, void>({
            query: () => ({
                url: `/auth/GetUser`,
                method: "GET",
            }),
            keepUnusedDataFor: 3600,
        }),
        trackToolSuggestion: builder.mutation<
            void,
            {
                action: "shown" | "clicked" | "reused";
                value?: number;
                durationMs?: number;
                toolKey?: string;
                toolIds?: string[];
            }
        >({
            query: (body) => ({
                url: `/api/metrics/tool-suggestion`,
                method: "POST",
                body,
            }),
        }),
        trackReference: builder.mutation<
            void,
            {
                action: "opened";
                referenceKey?: string;
                surface: "list" | "inline" | "considered";
                position?: number;
                sourceType?: string;
            }
        >({
            query: (body) => ({
                url: `/api/metrics/reference`,
                method: "POST",
                body,
            }),
        }),
        trackOpened: builder.mutation<
            void,
            {
                entity: "topic" | "thread" | "portal" | "news" | "filling";
                key?: string;
                source?:
                    | "search_tools"
                    | "search_sources"
                    | "userwork"
                    | "sidebar"
                    | "search"
                    | "frontpage"
                    | "checklist"
                    | "portal"
                    | "news";
                topicType?: string;
                position?: number;
                isFile?: boolean;
                portalType?: string;
                portalId?: string;
                section?: "lastvisited" | "pinned";
                /** Topic opens only (required there): see features/content/topicEntryNote.ts. */
                entrySource?: TopicEntrySource;
                surface?: "page" | "island";
                segmentId?: string;
                sectionId?: string;
            }
        >({
            query: (body) => ({
                url: `/api/metrics/open`,
                method: "POST",
                body,
            }),
        }),
        trackFiltered: builder.mutation<
            void,
            {entity: "portal" | "news"; filter?: string}
        >({
            query: (body) => ({
                url: `/api/metrics/filter`,
                method: "POST",
                body,
            }),
        }),
        trackDocument: builder.mutation<
            void,
            {
                action: "sent_to_dflow";
                value?: number;
                documentKey?: string;
                mode?: "standalone" | "collection";
                combine?: boolean;
                templateId?: string;
            }
        >({
            query: (body) => ({
                url: `/api/metrics/document`,
                method: "POST",
                body,
            }),
        }),
        trackAnswer: builder.mutation<
            void,
            {
                action: "stopped" | "copied" | "failed" | "followup_clicked";
                value?: number;
                durationMs?: number;
                sourceCount?: number;
                toolCount?: number;
                artifactCount?: number;
                reason?: string;
                firstTokenSeen?: boolean;
                position?: number;
            }
        >({
            query: (body) => ({
                url: `/api/metrics/answer`,
                method: "POST",
                body,
            }),
        }),
        trackInteraction: builder.mutation<
            void,
            {
                action:
                    | "search_submitted"
                    | "search_completed"
                    | "search_failed"
                    | "search_results_shown"
                    | "search_result_opened"
                    | "content_link_opened"
                    | "export_requested"
                    | "export_completed"
                    | "export_failed"
                    | "file_download_requested"
                    | "file_download_started"
                    | "filling_share_opened"
                    | "filling_sharing_updated"
                    | "email_copied"
                    | "email_handoff_requested";
                eventId: string;
                occurredAtUtc: string;
                searchId?: string;
                resultSetId?: string;
                operationId?: string;
                subjectId?: string;
                resultType?: "topic" | "file" | "external" | "unknown";
                linkKind?:
                    | "dotted"
                    | "exmark"
                    | "definition"
                    | "footnote"
                    | "footnote_edu";
                source?: "content" | "checklist" | "search_result";
                format?: "docx" | "xlsx" | "pdf" | "zip" | "unknown";
                contentType?: "checklist" | "templates";
                delivery?: "download" | "email";
                reason?: "network_error" | "service_unavailable" | "unknown";
                position?: number;
                resultCount?: number;
                durationMs?: number;
                addedCount?: number;
                removedCount?: number;
                failedCount?: number;
                outcome?: "full" | "partial" | "failed";
                mailtoBodyIncluded?: boolean;
            }
        >({
            query: (body) => ({
                url: `/api/metrics/interaction`,
                method: "POST",
                body,
            }),
        }),
    }),
});
